import React, { useCallback } from 'react';
import type { LayoutChangeEvent } from 'react-native';
import xor from 'lodash/xor';

import useHotkeys from '@lyearn/core/hooks/useHotkeys';
import Platform from '@lyearn/core/utils/Platform';
import { cn } from '@lyearn/style';
import { BaseButton, Box, Stack } from '@lyearn/ui';
import { FIRST_KEYCODE_INDEX } from '@/modules/corporate/modules/questions/fields/components/Player/OptionsListItem/consts';
import {
  renderKeyCode,
  renderOptionText,
} from '@/modules/corporate/modules/questions/fields/components/Player/OptionsListItem/renderers';

import { getElementListItemType } from '../../../../../utils';
import { getRightElementConfig } from '../../utils';
import { RightElementItemProps } from './types';

const isNative = Platform.OS !== 'web';

const EMPTY_ARRAY: never[] = [];

const RightElementItem: React.FC<RightElementItemProps> = (props) => {
  const {
    element,
    index,
    selectedElementId,
    isQuestionSubmitted,
    mtfResponse,
    setSelectedElementLayouts,
    correctMtfPairings,
    setSelectedElementId,
    onChange,
  } = props;

  const [layout, setLayout] = React.useState<LayoutChangeEvent['nativeEvent']['layout']>();
  const { type: rightElementType } = getElementListItemType({
    element,
    selectedElementId,
    isQuestionSubmitted,
    mtfResponse,
    correctMtfPairings,
    side: 'right',
  });
  const rightElementConfig = getRightElementConfig(rightElementType);
  const rightElementKeyCode = String.fromCharCode(FIRST_KEYCODE_INDEX + index);
  const onLayout = React.useCallback(
    ({ nativeEvent }: any) => setLayout(nativeEvent.layout),
    [setLayout],
  );

  const onClickRightElement = useCallback(() => {
    if (!selectedElementId) {
      return;
    }

    const updatedMtfResponse = mtfResponse.filter(
      (response) =>
        response.rightElementIds.findIndex((rightElementId) => rightElementId === element.id) ===
        -1,
    );

    onChange([
      ...updatedMtfResponse,
      { leftElementId: selectedElementId, rightElementIds: [element.id] },
    ]);
    setSelectedElementLayouts &&
      setSelectedElementLayouts((prevValue = {}) => {
        const connectedElements = prevValue[selectedElementId].connectedElements ?? EMPTY_ARRAY;

        return {
          ...prevValue,
          [selectedElementId]: {
            ...prevValue[selectedElementId],
            connectedElements: xor(connectedElements, [
              { [element.id]: { id: element.id, layout } },
            ]),
          },
        };
      });
    setSelectedElementId('');
  }, [
    selectedElementId,
    mtfResponse,
    onChange,
    element.id,
    setSelectedElementId,
    setSelectedElementLayouts,
    layout,
  ]);

  useHotkeys(rightElementKeyCode, onClickRightElement, [
    onClickRightElement,
    index,
    rightElementKeyCode,
  ]);

  const Component = isNative ? BaseButton : Stack;

  return (
    <Component
      className={cn(
        'flex w-full min-w-0 flex-row items-center overflow-hidden p-0',
        rightElementConfig.classes.listItem,
        {
          'pointer-events-none': isQuestionSubmitted,
        },
        props.containerClassName,
      )}
      data-mtf-id={element.id}
      // @ts-ignore
      role={isQuestionSubmitted ? '' : 'button'}
      style={isNative ? rightElementConfig.styles?.listItem : undefined}
      onClick={onClickRightElement}
      onLayout={onLayout}>
      {renderKeyCode({
        className: rightElementConfig.classes.keyCode,
        children: rightElementConfig.renderKeyCodeContent(rightElementKeyCode),
        style: isNative ? rightElementConfig.classes.keyCode : undefined,
      })}
      <Box className="min-w-0 flex-1">
        {renderOptionText({
          optionText: element.text,
          className: rightElementConfig.classes.optionText,
          secondaryBackgroundColor: rightElementConfig.classes.secondaryBackgroundColor,
        })}
      </Box>
    </Component>
  );
};

export default RightElementItem;
